/**
 * MultiSelect component for selecting one or more options, supporting grouped/sectioned items and custom display.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.label] - The label for the select input.
 * @param {string} [props.placeholder] - Placeholder text for the select.
 * @param {Array} [props.validation] - Validation rules for the field.
 * @param {boolean} [props.required] - Whether the field is required.
 * @param {string} [props.className] - Additional CSS classes for the input.
 * @param {boolean} [props.isStage] - If true, uses custom Option rendering for stage dropdowns.
 * @param {boolean} [props.isMulti] - If true, enables multi-select mode.
 * @param {Function} [props.onChangeHandler] - Handler for selection changes.
 * @param {boolean} [props.isSearchable] - If true, enables search in the dropdown.
 * @param {boolean} [props.setDefaultOption] - If true, adds a default option at the top.
 * @param {string} [props.dropdownWidth] - Width of the dropdown menu.
 * @param {Array} [props.items] - The options/items to display in the dropdown.
 * @param {Array} [props.value] - The currently selected value(s).
 * @param {boolean} [props.error] - If true, shows error message.
 * @param {boolean} [props.touched] - If true, shows error message if error exists.
 * @param {boolean} [props.filterVariationsByStatus] - If true, filters out DRAFT and SUBMITTED variations from the options.
 * @returns {JSX.Element}
 *
 * Use case: Used for selecting one or more tasks, sections, or items in forms, with support for grouped display and custom tags.
 * When filterVariationsByStatus is true, only PENDING and APPROVED variations will be shown (used for progress claims, budget, PO/WO, Scheduling/Time sheets).
 */

import React from 'react';
import Select, { components } from 'react-select';
import './task-multi-select.scss';
import _ from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import { DefaultSectionOptions } from '../../util/enums';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckIcon from '@mui/icons-material/Check';

/** Indentation in px per nesting level for hierarchical display */
const TREE_INDENT_PX = 20;
const SELECT_MENU_Z_INDEX = 2;
const TOOLTIP_Z_INDEX = 3;

// Custom dropdown option renderer for sectioned/staged dropdowns.
// Supports hierarchical indentation (depth) and attribution icons.
const Option = ({ children, ...props }) => {
  const {
    depth,
    displayName,
    isSectionHeader,
    isStage,
    isAttributionLevel,
    is_attribution_level,
    isSection,
    hasContractorWork,
  } = props.data;
  const ContractorWorkIcon = props.selectProps.contractorWorkIcon;
  const contractorWorkTooltip =
    props.selectProps.contractorWorkTooltip || 'Contains contractor work';
  const showContractorWorkIcon = Boolean(
    ContractorWorkIcon && hasContractorWork && !isSectionHeader && !isStage && !isSection,
  );

  // Check if we have tree structure metadata
  const hasTreeMetadata = typeof depth === "number";
  const showAttributionIcon = !!(isAttributionLevel || is_attribution_level);

  if (hasTreeMetadata) {
    const itemName = displayName || props.data.label || String(children);
    const indentPx = depth * TREE_INDENT_PX;
    const isBold = isSectionHeader || isStage || isSection;
    const textColor = isSectionHeader && !isSection ? "#888" : "inherit";

    return (
      <components.Option {...props} key={props.data.value}>
        <div
          data-cy={`option-${props.data.value}`}
          data-attribution={isAttributionLevel ? "true" : "false"}
          data-section={isSection ? "true" : "false"}
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            paddingLeft: indentPx,
            gap: '8px',
            width: '100%'
          }}
        >
          {showAttributionIcon ? (
            <AssignmentTurnedInIcon
              fontSize="small"
              color={props.isSelected ? "inherit" : "primary"}
              titleAccess="Attribution Level"
            />
          ) : (
            props.isSelected && (
              <CheckIcon
                fontSize="small"
                sx={{ color: 'var(--brand-color-2)' }}
              />
            )
          )}
          {showContractorWorkIcon && (
            <Tooltip
              title={contractorWorkTooltip}
              placement="top"
              arrow
              slotProps={{
                popper: {
                  sx: {
                    zIndex: TOOLTIP_Z_INDEX,
                  },
                },
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--brand-color-1)',
                }}
              >
                <ContractorWorkIcon fontSize="small" />
              </span>
            </Tooltip>
          )}
          <span
            style={{
              fontWeight: isBold ? 'bold' : 'normal',
              color: textColor,
              flex: 1
            }}
          >
            {itemName}
          </span>
          {(showAttributionIcon && (props.isSelected || props.data.isCovered)) && (
            <CheckIcon
              fontSize="small"
              sx={{ color: 'var(--brand-color-2)', ml: 'auto' }}
            />
          )}
        </div>
      </components.Option>
    );
  }

  // Fallback: Legacy display without tree metadata
  const childText = typeof children === "string" ? children : String(children);
  const parts = childText.split(" > ");
  const lastPart = parts.pop();
  const restOfPath = parts.join(" > ");

  return (
    <components.Option {...props} key={props.data.value}>
      <div
        data-cy={`option-${props.data.value}`}
        data-attribution={isAttributionLevel ? "true" : "false"}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
      >
        {showAttributionIcon ? (
          <AssignmentTurnedInIcon
            sx={{ fontSize: '1rem' }}
            color={(props.isSelected || props.data.isCovered) ? "inherit" : "primary"}
          />
        ) : (
          (props.isSelected || props.data.isCovered) && (
            <CheckIcon
              fontSize="small"
              sx={{ color: 'var(--brand-color-2)' }}
            />
          )
        )}
        {showContractorWorkIcon && (
          <Tooltip
            title={contractorWorkTooltip}
            placement="top"
            arrow
            slotProps={{
              popper: {
                sx: {
                  zIndex: TOOLTIP_Z_INDEX,
                },
              },
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'var(--brand-color-1)',
              }}
            >
              <ContractorWorkIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
        <div style={{ flex: 1 }}>
          <span>{restOfPath ? restOfPath + ' > ' : ''}</span>
          <strong className='section-dropdown-lastpart'>{lastPart}</strong>
        </div>
        {showAttributionIcon && (props.isSelected || props.data.isCovered) && (
          <CheckIcon
            fontSize="small"
            sx={{ color: 'var(--brand-color-2)', ml: 'auto' }}
          />
        )}
      </div>
    </components.Option>
  );
};

/**
 * Custom tag (multiValueLabel) display for selected items.
 * - Stages/sections (isStage / isAttributionLevel): use "parent > label" to avoid "BASEMENT (BASEMENT)".
 * - Tasks: use "label (stagePath)" e.g. "FIRE INTERNAL (BASEMENT)".
 * - Else: "parentLabel > label" or label only.
 */
const CustomMultiValueLabel = (props) => {
  const { data } = props;
  const label = data.displayName || data.label; // Prefer short name if available
  const hasLevel = data.stagePath || data.parentLabel;
  const displayText = hasLevel
    ? data.stagePath
      ? `${label} (${data.stagePath})`
      : `${label} (${data.parentLabel})`
    : data.parentLabel
      ? `${data.parentLabel} > ${label}`
      : (label ?? data.value);

  return (
    <components.MultiValueLabel {...props}>
      <span>{displayText}</span>
    </components.MultiValueLabel>
  );
};

/**
 * Groups items with their parent section/label for grouped dropdown display.
 * Adds a parentLabel property to each item for display in tags and options.
 * @param {Array} items - The items to group.
 * @returns {Array} Grouped items with parentLabel property.
 */
const groupItemsWithParents = (items) => {
  let currentParent = null;
  return items.map((item) => {
    // If an item is at depth 0, it's a root item/section and should reset the current parent
    if (item.depth === 0) {
      currentParent = null;
    }

    if (item.isDisabled) {
      currentParent = item.label;
      return item;
    }
    return {
      ...item,
      parentLabel: currentParent,
    };
  });
};

/**
 * Main MultiSelect component definition.
 * Handles grouped options, custom tag rendering, and value normalization for react-select.
 */
export default function TaskMultiSelect({
  label = "",
  placeholder = "",
  validation = [],
  required,
  className = "",
  isStage = false,
  isMulti = false,
  onChangeHandler,
  isSearchable = true,
  setDefaultOption = false,
  dropdownWidth = "100%",
  filterVariationsByStatus = false, // New prop to filter variations by status
  dataCy,
  staticDropdown = false,
  contractorWorkIcon,
  contractorWorkTooltip = 'Contains contractor work',
  ...props
}) {
  // Determine if the field is required based on validation rules
  const isRequired =
    required ||
    (validation.includes("required") &&
      !(validation.includes("skip") || validation.includes("optional")));

  /**
   * Custom styles for react-select component, including focus, multi-value, and menu styling.
   */
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      height: 'auto',
      minHeight: '45px',
      width: isMulti ? '100%' : 'max-content',
      minWidth: '100%',
      borderColor: '#e2e2e2',
      borderRadius: '8px',
      ...(state.isFocused && {
        borderColor: "#86b7fe",
        outline: "0",
        boxShadow: "0 0 0 0.25rem rgba(13, 110, 253, 0.25)",
      }),
    }),
    placeholder: (provided) => ({ ...provided, textAlign: "left" }),
    option: (provided, state) => ({
      ...provided,
      textAlign: 'left',
      backgroundColor: (state.isSelected || state.data.isCovered)
        ? 'rgba(64, 185, 195, 0.08)'
        : state.isFocused
          ? 'rgba(0, 0, 0, 0.04)'
          : 'transparent',
      color: (state.isSelected || state.data.isCovered) ? 'var(--brand-color-1)' : provided.color,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'rgba(64, 185, 195, 0.15)',
      },
    }),
    menu: (provided) => ({ ...provided, width: dropdownWidth }),
    menuPortal: (provided) => ({ ...provided, zIndex: SELECT_MENU_Z_INDEX }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "var(--brand-color-4)",
      color: "white",
    }),
    multiValueLabel: (provided) => ({ ...provided, color: "white" }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      ":hover": {
        backgroundColor: "var(--brand-color-3)",
        color: "white",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      maxHeight: isMulti ? '100px' : 'none',
      overflowY: isMulti ? 'auto' : 'visible',
      overflow: isMulti ? undefined : 'visible',
      flexWrap: isMulti ? 'wrap' : 'nowrap',
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'visible',
      textOverflow: 'clip',
      maxWidth: 'none',
      position: 'relative',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      color: "var(--brand-color-3)",
    }),
    indicatorSeparator: (provided) => ({ ...provided }),
  };

  /**
   * Optionally adds a default option (e.g., 'Select Section') at the top of the dropdown.
   */
  const defaultOption = setDefaultOption
    ? [
      {
        label: DefaultSectionOptions.LABEL,
        value: DefaultSectionOptions.VALUE,
      },
    ]
    : [];

  /**
   * Group items for display (with parent section/label) and combine with default option if set.
   * Also filter variations by status if filterVariationsByStatus is true.
   */
  const groupedItems = groupItemsWithParents(
    filterVariationsByStatus
      ? (props.items || []).filter((item) => {
        // Only allow PENDING and APPROVED variations
        if (item.variationStatus) {
          return ["PENDING", "APPROVED"].includes(item.variationStatus);
        }
        // Check legacy status fields
        if (item.proposal_dealt === false) {
          return false; // This is DRAFT
        }
        if (item.proposal_status) {
          return ["PENDING", "APPROVED"].includes(item.proposal_status);
        }
        return true;
      })
      : props.items || [],
  );
  /**
   * Smart dropdown filtering with cascading roll-up:
   *
   * 1. Selected items are "covered" (hidden from dropdown, shown as pills).
   * 2. Descendants of a selected section are "covered" (children collapse).
   * 3. Roll-up: if ALL selectable descendants of a parent are covered,
   *    the parent itself becomes covered. This cascades upward until stable.
   * 4. All covered items and their subtrees are hidden from the dropdown.
   *
   * Example: Selecting Task 1 + Task 2 → Drainage covered (roll-up) →
   *          Sewer covered (roll-up) → entire Sewer tree hidden.
   */
  const getFilteredOptions = () => {
    // --- Step 1: Collect selected values and identify section selections ---
    const selectedValues = new Set();
    const selectedSectionValues = new Set();

    const values = Array.isArray(props.value)
      ? props.value
      : props.value
        ? [props.value]
        : [];
    values.forEach((v) => {
      const val = typeof v === "object" ? v : { value: v };
      selectedValues.add(val.value);
      if (val.isSection || val.isStage) {
        selectedSectionValues.add(val.value);
      }
    });

    // No selections → full list
    if (selectedValues.size === 0) return groupedItems;

    // --- Step 2: Base coverage — directly selected + descendants of selected sections ---
    const coveredSet = new Set(); // index-based
    let collapseDepth = null;

    for (let i = 0; i < groupedItems.length; i++) {
      const item = groupedItems[i];
      const itemDepth = item.depth ?? 0;

      // Exited a selected section's subtree?
      if (collapseDepth !== null && itemDepth <= collapseDepth) {
        collapseDepth = null;
      }

      // Inside a selected section's subtree → covered
      if (collapseDepth !== null && itemDepth > collapseDepth) {
        coveredSet.add(i);
        continue;
      }

      // Directly selected → covered
      if (selectedValues.has(item.value)) {
        coveredSet.add(i);
        if (selectedSectionValues.has(item.value)) {
          collapseDepth = itemDepth;
        }
      }
    }

    // --- Step 3: Cascading roll-up ---
    // A parent is "fully covered" when ALL its selectable descendants are covered.
    // When a parent becomes covered, ITS parent may now also be fully covered.
    // Repeat until no new items are marked.
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i < groupedItems.length; i++) {
        if (coveredSet.has(i)) continue;

        const item = groupedItems[i];
        const itemDepth = item.depth ?? 0;

        // Look ahead at all descendants within this subtree
        let hasSelectableDescendant = false;
        let allCovered = true;

        for (let j = i + 1; j < groupedItems.length; j++) {
          const desc = groupedItems[j];
          const descDepth = desc.depth ?? 0;
          if (descDepth <= itemDepth) break; // Exited subtree

          // Only check selectable items (skip disabled headers)
          const isSelectable = !desc.isDisabled && !desc.isSectionHeader;
          if (isSelectable) {
            hasSelectableDescendant = true;
            if (!coveredSet.has(j)) {
              allCovered = false;
              break;
            }
          }
        }

        // All selectable children covered → this parent is covered via roll-up
        if (hasSelectableDescendant && allCovered) {
          coveredSet.add(i);
          changed = true;
        }
      }
    }

    // --- Step 4: Finalize options list ---
    // In legacy mode (staticDropdown=false), we actually filter out the covered items.
    // In premium mode (staticDropdown=true), we keep all items but mark them for styling.
    if (staticDropdown) {
      return groupedItems.map((item, i) => ({
        ...item,
        isCovered: coveredSet.has(i)
      }));
    }

    const result = [];
    let hideDepth = null;

    for (let i = 0; i < groupedItems.length; i++) {
      const item = groupedItems[i];
      const itemDepth = item.depth ?? 0;

      // Exited the hidden subtree?
      if (hideDepth !== null && itemDepth <= hideDepth) {
        hideDepth = null;
      }

      // Still inside a hidden subtree → skip
      if (hideDepth !== null) continue;

      // This item is covered → start hiding from this depth
      if (coveredSet.has(i)) {
        hideDepth = itemDepth;
        continue;
      }

      result.push(item);
    }

    return result;
  };

  const options = [
    ...defaultOption,
    ...(isMulti ? getFilteredOptions() : groupedItems),
  ];

  /**
   * Normalizes the value prop to ensure parentLabel is included for display.
   * This ensures that selected values match the options structure (with parentLabel).
   * Handles both single and multi-select modes.
   * Note: We search in groupedItems (full list) not options (filtered) so selected values display correctly.
   */
  const allOptionsWithParent = [...defaultOption, ...groupedItems];
  const matchOptionFromOptions = (val) => {
    if (val === null || val === undefined) return null;
    // Extract ID: could be the value itself (if primitive) or .value (if object)
    const searchVal = typeof val === "object" && val !== null ? val.value : val;
    if (searchVal === null || searchVal === undefined) return null;

    return allOptionsWithParent.find((opt) => opt.value == searchVal) || val;
  };

  const normalizedValue = isMulti
    ? Array.isArray(props.value)
      ? props.value.map(matchOptionFromOptions).filter(Boolean)
      : []
    : Array.isArray(props.value)
      ? matchOptionFromOptions(props.value[0])
      : matchOptionFromOptions(props.value);

  /**
   * Handles selection changes and calls the provided onChangeHandler with normalized values.
   * @param {Array|Object} selected - The selected option(s) from react-select.
   */
  const handleChange = (selected, actionMeta) => {
    // Prevent unselecting an already selected item from the dropdown menu (deselect-option)
    // This addresses the user request to remove the unselect-on-click behavior in the menu.
    if (actionMeta && actionMeta.action === 'deselect-option') {
      return;
    }

    if (isMulti) {
      onChangeHandler(Array.isArray(selected) ? selected : []);
    } else {
      onChangeHandler(selected || null);
    }
  };

  /**
   * Custom group label renderer for grouped dropdowns, showing section name and count.
   * @param {Object} data - Group data from react-select.
   * @returns {JSX.Element}
   */
  const formatGroupLabel = (data) => (
    <div className="group-styles">
      <span>{data.label}</span>
      {!isStage && <span className="group-badge">{data.options.length}</span>}
    </div>
  );

  /**
   * Renders the MultiSelect input with react-select, including label, error, and all customizations.
   * If a parentLabel exists for the selected single value, display it as a badge above the select.
   */
  return (
    <div className="form-group w-100">
      <div
        className={_.uniq(["me-3 input-block", ...className.split(" ")]).join(
          " ",
        )}
      >
        {label && (
          <label className="input-label">
            <span>
              {label} {isRequired && <span className="text-danger">*</span>}
            </span>
          </label>
        )}

        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} data-cy={`${dataCy}`}>
          {!isMulti && normalizedValue && (normalizedValue.stagePath || normalizedValue.parentLabel) && (
            <div
              className='parent-label-badge bg-brand-2'
            // onMouseEnter={() => setShowTooltip(true)}
            // onMouseLeave={() => setShowTooltip(false)}
            >
              {normalizedValue.stagePath || normalizedValue.parentLabel}
              {/* {showTooltip && (
                <div
                  className='task-multi-select-tooltip'
                >
                  {normalizedValue.stagePath || normalizedValue.parentLabel}
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '6px solid #333',
                    }}
                  />
                </div>
              )} */}
            </div>
          )}

          <div className="w-100">
            <Select
              isMulti={isMulti}
              options={options}
              value={normalizedValue}
              onChange={handleChange}
              contractorWorkIcon={contractorWorkIcon}
              contractorWorkTooltip={contractorWorkTooltip}
              styles={customStyles}
              placeholder={placeholder || label || 'Select an option'}
              formatGroupLabel={formatGroupLabel}
              components={{
                Option,
                ...(isMulti ? { MultiValueLabel: CustomMultiValueLabel } : {}),
              }}
              isSearchable={isSearchable}
              hideSelectedOptions={!staticDropdown}
              noOptionsMessage={() => 'No options available'}
              menuPortalTarget={document.body}
              data-cy={`${dataCy}`}
            />
          </div>
        </div>
        {props.error && props.touched && (
          <small className="float-start text-danger">{props.error}</small>
        )}
      </div>
    </div>
  );
}
